import { useEffect, useState } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';

import { AnalyticsEmpty, AnalyticsError, AnalyticsLoading } from '../../components/AnalyticsState';
import { SectionSubtitle, SectionTitle } from '../../components/analyticsUi';
import InfoTooltip from 'components/v2/common/infoTooltip';

import {
    OPERATIONS_TEAM_ACTIVE,
    OPERATIONS_TEAM_AVG_PROCESSING,
    OPERATIONS_TEAM_COMPLETED,
    OPERATIONS_TEAM_DOCUMENTS_UPLOADED,
    OPERATIONS_TEAM_LEVEL,
    OPERATIONS_TEAM_MEMBERS,
    OPERATIONS_TEAM_MESSAGES_POSTED,
} from '../../constants/metricDescriptions';
import useRoleTypeTabs, { ROLE_TYPE_LABELS } from '../../hooks/useRoleTypeTabs';
import {
    getApplicantDocumentUploads,
    getApplicantPostedMessages,
    getPractitionerDocumentUploads,
    getPractitionerMessagesPosted,
    getPractitionerSpeeds,
} from 'api/rest/analytics';
import { AnalyticsRoleType } from 'hooks/analytics';

interface RoleOperationsSummary {
    roleType: AnalyticsRoleType | 'client';
    label: string;
    totalPractitioners: number | null;
    avgProcessingDays: number | null;
    totalCasesCompleted: number | null;
    totalCasesActive: number | null;
    totalDocumentsUploaded: number;
    totalMessagesPosted: number;
}

interface RoleLevelOperationsProps {
    from?: string;
    to?: string;
    serviceProviderId?: string;
}

/** Same palette as the assessment overview cards. */
const TEAM_COLOR: Record<RoleOperationsSummary['roleType'], string> = {
    consultant: '#2563EB',
    backoffice: '#7C3AED',
    sales: '#D97706',
    client: '#059669',
};

const StatLabel = ({ label, hint }: { label: string; hint: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
            {label}
        </Typography>
        <InfoTooltip title={hint} size={13} />
    </Box>
);

const formatCount = (value: number | null) => (value == null ? '—' : String(value));

const formatDays = (value: number | null) => (value == null ? '—' : `${value.toFixed(1)}d`);

const RoleLevelOperations = ({ from, to, serviceProviderId }: RoleLevelOperationsProps) => {
    const { allowedTabs } = useRoleTypeTabs();
    const [summaries, setSummaries] = useState<RoleOperationsSummary[]>([]);
    const [loading, setLoading] = useState<boolean>(allowedTabs.length > 0);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (allowedTabs.length === 0) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);

        Promise.all([
            Promise.all(
                allowedTabs.map(async (roleType) => {
                    const [speedsRes, docsRes, msgsRes] = await Promise.all([
                        getPractitionerSpeeds(roleType, from, to, serviceProviderId),
                        getPractitionerDocumentUploads(roleType, from, to, serviceProviderId),
                        getPractitionerMessagesPosted(roleType, from, to, serviceProviderId),
                    ]);

                    const speeds = speedsRes.data.practitionerSpeeds ?? [];
                    const docs = docsRes.data.practitionerDocumentUploads ?? [];
                    const msgs = msgsRes.data.practitionerMessagesPosted ?? [];
                    const avgProcessingDays =
                        speeds.length > 0
                            ? speeds.reduce((sum, row) => sum + row.avgProcessingDays, 0) /
                              speeds.length
                            : 0;

                    return {
                        roleType,
                        label: ROLE_TYPE_LABELS[roleType],
                        totalPractitioners: speeds.length,
                        avgProcessingDays,
                        totalCasesCompleted: speeds.reduce(
                            (sum, row) => sum + row.casesCompleted,
                            0
                        ),
                        totalCasesActive: speeds.reduce((sum, row) => sum + row.casesActive, 0),
                        totalDocumentsUploaded: docs.reduce(
                            (sum, row) => sum + row.documentsUploaded,
                            0
                        ),
                        totalMessagesPosted: msgs.reduce((sum, row) => sum + row.messagesPosted, 0),
                    };
                })
            ),
            getApplicantDocumentUploads(from, to, serviceProviderId),
            getApplicantPostedMessages(from, to, serviceProviderId),
        ])
            .then(([roleSummaries, clientDocsRes, clientMsgsRes]) => {
                if (cancelled) return;
                const clientDocs = clientDocsRes.data.applicantDocumentUploads ?? [];
                const clientMsgs = clientMsgsRes.data.applicantPostedMessages ?? [];
                setSummaries([
                    ...roleSummaries,
                    {
                        roleType: 'client',
                        label: 'Clients',
                        totalPractitioners: null,
                        avgProcessingDays: null,
                        totalCasesCompleted: null,
                        totalCasesActive: null,
                        totalDocumentsUploaded: clientDocs.reduce(
                            (sum, row) => sum + row.documentsUploaded,
                            0
                        ),
                        totalMessagesPosted: clientMsgs.reduce(
                            (sum, row) => sum + row.messagesPosted,
                            0
                        ),
                    },
                ]);
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(
                        err?.response?.data?.message ||
                            err.message ||
                            'Failed to load role operations.'
                    );
                    setSummaries([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [allowedTabs, from, to, serviceProviderId]);

    if (allowedTabs.length === 0) return null;

    const columnCount = Math.min(Math.max(summaries.length, 1), 4);

    return (
        <Box component="section" aria-labelledby="team-operations-title" sx={{ mb: 2 }}>
            <SectionTitle>
                <span id="team-operations-title">Team Operations</span>
                <InfoTooltip title={OPERATIONS_TEAM_LEVEL} />
            </SectionTitle>
            <SectionSubtitle>
                Documents, messages, and case speed for each team in the selected period.
            </SectionSubtitle>

            {loading ? (
                <AnalyticsLoading minHeight={180} />
            ) : error ? (
                <AnalyticsError message={error} />
            ) : summaries.length === 0 ? (
                <AnalyticsEmpty
                    title="No teams to show"
                    description="There is no team activity for the selected period."
                />
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: `repeat(${columnCount}, minmax(0, 1fr))`,
                        },
                        gap: 1.25,
                    }}
                >
                    {summaries.map((role) => {
                        const color = TEAM_COLOR[role.roleType];
                        const activity = [
                            {
                                label: 'Documents uploaded',
                                value: String(role.totalDocumentsUploaded),
                                hint: OPERATIONS_TEAM_DOCUMENTS_UPLOADED,
                            },
                            {
                                label: 'Messages posted',
                                value: String(role.totalMessagesPosted),
                                hint: OPERATIONS_TEAM_MESSAGES_POSTED,
                            },
                        ];
                        const footer = [
                            {
                                label: 'Avg days',
                                value: formatDays(role.avgProcessingDays),
                                hint: OPERATIONS_TEAM_AVG_PROCESSING,
                            },
                            {
                                label: 'Completed',
                                value: formatCount(role.totalCasesCompleted),
                                hint: OPERATIONS_TEAM_COMPLETED,
                            },
                            {
                                label: 'Active',
                                value: formatCount(role.totalCasesActive),
                                hint: OPERATIONS_TEAM_ACTIVE,
                            },
                        ];
                        const memberLabel =
                            role.totalPractitioners == null
                                ? null
                                : role.totalPractitioners === 1
                                  ? '1 member'
                                  : `${role.totalPractitioners} members`;

                        return (
                            <Paper
                                key={role.roleType}
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    borderColor: 'divider',
                                    borderTop: `3px solid ${color}`,
                                    p: 1.5,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                                >
                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                        {role.label}
                                    </Typography>
                                    {memberLabel && (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontVariantNumeric: 'tabular-nums' }}
                                            >
                                                {memberLabel}
                                            </Typography>
                                            <InfoTooltip
                                                title={OPERATIONS_TEAM_MEMBERS}
                                                size={13}
                                            />
                                        </Box>
                                    )}
                                </Stack>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                        gap: 1.5,
                                        mt: 1.5,
                                    }}
                                >
                                    {activity.map((metric) => (
                                        <Box key={metric.label}>
                                            <StatLabel label={metric.label} hint={metric.hint} />
                                            <Typography
                                                sx={{
                                                    color,
                                                    fontSize: { xs: 24, sm: 27, lg: 24 },
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.03em',
                                                    lineHeight: 1.15,
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}
                                            >
                                                {metric.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                        gap: 0.75,
                                        mt: 1.25,
                                        pt: 1,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    {footer.map((metric) => (
                                        <Box key={metric.label}>
                                            <StatLabel label={metric.label} hint={metric.hint} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}
                                            >
                                                {metric.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default RoleLevelOperations;
